import React from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';

export type BannerVariant = 'error' | 'warning' | 'info' | 'success' | 'accent';

export interface BannerConfig {
    message: string;
    variant?: BannerVariant;
    badge?: string;
    action?: React.ReactNode;
}

interface BannerProps {
    children: React.ReactNode;
    variant?: BannerVariant;
    badge?: string;
    action?: React.ReactNode;
}

const variantStyles: Record<
    BannerVariant,
    { bg: string; border: string; badgeBg: string; text: string; badgeBorder: string }
> = {
    error: {
        bg: Colors.errorBackground,
        border: Colors.errorBackground,
        badgeBg: Colors.errorBackground,
        text: Colors.error,
        badgeBorder: Colors.error,
    },
    warning: {
        bg: Colors.warningBackground,
        border: Colors.warningBackground,
        badgeBg: Colors.warningBackground,
        badgeBorder: Colors.warning,
        text: Colors.warning,
    },
    info: {
        bg: Colors.lightTurq,
        border: Colors.lightTurq,
        badgeBg: Colors.lightTurq,
        text: Colors.darkNavy,
        badgeBorder: Colors.primary,
    },
    success: {
        bg: Colors.lightTurq,
        border: Colors.mediumTurq,
        badgeBg: Colors.mediumTurq,
        text: Colors.turquoise,
        badgeBorder: Colors.mediumTurq,
    },
    accent: {
        bg: Colors.accent,
        border: Colors.accent,
        badgeBg: Colors.white,
        text: Colors.white,
        badgeBorder: Colors.accent,
    },
};

export function Banner({ children, variant = 'warning', badge, action }: BannerProps) {
    const styles = variantStyles[variant];

    return (
        <BannerContainer style={{ backgroundColor: styles.bg, borderBottomColor: styles.border }}>
            <Left>
                {badge && (
                    <Badge
                        style={{
                            backgroundColor: styles.badgeBg,
                            color: styles.text,
                            border: `1px solid ${styles.badgeBorder}`,
                        }}
                    >
                        {badge}
                    </Badge>
                )}
                <Message style={{ color: styles.text }}>{children}</Message>
            </Left>
            {action && <ActionWrapper>{action}</ActionWrapper>}
        </BannerContainer>
    );
}

const BannerContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 24px',
    borderBottom: '1px solid',
    margin: '-24px -24px 24px -24px',
    [theme.breakpoints.down('md')]: {
        margin: '-12px -12px 12px -12px',
    },
}));

const Left = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
});

const Badge = styled('span')({
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
});

const Message = styled('span')({
    fontSize: 13,
});

const ActionWrapper = styled('div')({
    flexShrink: 0,
});
